/**
 * Utility class for calculating and managing week dividers in lists.
 * Used to group items by calendar week with divider headers.
 */
export class WeekDividerUtils {

    /**
     * Calculates ISO week number for a Date object.
     * Uses the ISO 8601 definition where weeks start on Monday and
     * week 1 is the week containing the first Thursday of the year.
     */
    static getWeekNumber(date: Date): number {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    }

    /**
     * Calculates ISO week number from a date string (yyyy-MM-dd or ISO format).
     */
    static getWeekNumberFromString(dateStr: string): number {
        if (!dateStr) { return 0; }
        const date = new Date(dateStr);
        return this.getWeekNumber(date);
    }

    /**
     * Calculates ISO week number from a datetime string, extracting just the date part.
     */
    static getWeekNumberFromDatetime(datetime: string): number {
        if (!datetime) { return 0; }
        const dateStr = datetime.split('T')[0];
        return this.getWeekNumberFromString(dateStr);
    }

    /**
     * Interface for items that can have week divider information.
     */
    static addWeekDividerInfo<T>(
        items: T[],
        getDatetime: (item: T) => string
    ): (T & { weekNumber: number; showWeekDivider: boolean })[] {
        let previousWeek = 0;

        return items.map(item => {
            const datetime = getDatetime(item);
            const weekNumber = this.getWeekNumberFromDatetime(datetime);
            const showWeekDivider = previousWeek === 0 || weekNumber !== previousWeek;
            previousWeek = weekNumber;

            return {
                ...item,
                weekNumber,
                showWeekDivider
            };
        });
    }

    /**
     * Recalculates showWeekDivider for items that already have weekNumber.
     * Useful after filtering when week dividers need to be recalculated.
     */
    static recalculateWeekDividers<T extends { weekNumber: number; showWeekDivider: boolean }>(
        items: T[]
    ): void {
        let previousWeek = 0;
        for (const item of items) {
            item.showWeekDivider = previousWeek === 0 || item.weekNumber !== previousWeek;
            previousWeek = item.weekNumber;
        }
    }
}
